<template>
  <div>
    <div class="appui">
      <div class="appui-head">
        <div>
          <h1 class="appui-title">Pesquisa de Satisfação</h1>
          <div class="appui-crumb">App <span class="sep">/</span> Pesquisa</div>
        </div>
      </div>

      <div class="appui-tabs">
        <button :class="{ 'is-active': activeTab === 'perguntas' }" @click="activeTab = 'perguntas'"><i class="bx bx-list-check" style="margin-right:5px"></i>Perguntas</button>
        <button :class="{ 'is-active': activeTab === 'resultados' }" @click="switchTab('resultados')"><i class="bx bx-bar-chart-alt-2" style="margin-right:5px"></i>Resultados</button>
      </div>

      <!-- PERGUNTAS -->
      <template v-if="activeTab === 'perguntas'">
        <div v-if="!questions.length" class="appui-emptystate">
          <div class="appui-emptystate-icon"><i class="bx bx-list-check"></i></div>
          <div class="appui-emptystate-title">Nenhuma pergunta ainda</div>
          <div class="appui-emptystate-sub">Monte a pesquisa que o participante responde no app — escolha única, múltipla, texto livre ou nota.</div>
          <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Adicionar a primeira pergunta</button>
        </div>

        <section v-else class="appui-card appui-card--flush">
          <div class="appui-card-toolbar">
            <div class="appui-card-toolbar-left">
              <span class="appui-toolbar-hint"><i class="mdi mdi-drag-vertical"></i> Arraste para reordenar as perguntas.</span>
            </div>
            <div class="appui-card-toolbar-right">
              <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Nova pergunta</button>
            </div>
          </div>

          <table class="appui-table">
            <thead><tr><th style="width:46px"></th><th style="width:52px">Seq</th><th>Pergunta</th><th style="width:150px">Tipo</th><th style="width:100px">Obrigatória</th><th style="width:80px">Ativa</th><th style="width:150px;text-align:right">Ações</th></tr></thead>
            <draggable
              v-model="questions"
              :element="'tbody'"
              :options="{ animation: 180, handle: '.appui-grip', ghostClass: 'appui-drag-ghost' }"
              @end="reorder"
            >
              <tr v-for="q in questions" :key="q.id">
                <td><span class="appui-grip" title="Arraste para reordenar">⠿</span></td>
                <td><span class="appui-badge appui-badge--blue">{{ q.sequence }}</span></td>
                <td>
                  <div style="font-weight:500;color:#495057">{{ q.asking }}</div>
                  <div v-if="q.answers && q.answers.length" style="font-size:11.5px;color:#adb5bd">{{ q.answers.map(a => a.answer).join(' · ') }}</div>
                </td>
                <td><span class="appui-badge"><i :class="typeIcon(q.asking_type)" style="margin-right:4px"></i>{{ typeLabel(q.asking_type) }}</span></td>
                <td style="color:#74788d">{{ q.required_answer ? 'Sim' : 'Opcional' }}</td>
                <td><button class="appui-switch" :class="{ 'is-on': q.status }" @click="toggle(q)"><span class="knob"></span></button></td>
                <td style="text-align:right;white-space:nowrap">
                  <button class="appui-tbtn" style="margin-right:6px" @click="openEdit(q)">Editar</button>
                  <button class="appui-tbtn appui-tbtn--danger" @click="remove(q)">Excluir</button>
                </td>
              </tr>
            </draggable>
          </table>
        </section>
      </template>

      <!-- RESULTADOS -->
      <template v-if="activeTab === 'resultados'">
        <section class="appui-card appui-card--flush">
          <div class="appui-card-toolbar">
            <div class="appui-card-toolbar-left"><span class="appui-sec-title">Resultados agregados</span></div>
            <div class="appui-card-toolbar-right">
              <button class="appui-btn appui-btn--ghost appui-btn--sm" @click="exportResults()"><i class="mdi mdi-download mr-1"></i> Exportar</button>
              <button class="appui-btn appui-btn--ghost appui-btn--sm" @click="loadResults()"><i class="mdi mdi-refresh mr-1"></i> Atualizar</button>
            </div>
          </div>
          <div v-if="!results.length" class="appui-inline-empty">Sem respostas ainda.</div>
          <div v-else style="padding:16px;display:flex;flex-direction:column;gap:14px">
            <div v-for="r in results" :key="r.id" class="appui-survey-result">
              <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:10px">
                <span style="font-weight:600;font-size:13px;color:#495057">{{ r.asking }}</span>
                <span class="appui-badge appui-badge--blue" style="flex:none">{{ r.total_answers }} {{ r.total_answers === 1 ? 'resposta' : 'respostas' }}</span>
              </div>
              <template v-if="r.options">
                <div v-for="(o, i) in r.options" :key="i" style="margin-bottom:8px">
                  <div style="display:flex;justify-content:space-between;font-size:12px;color:#74788d;margin-bottom:2px">
                    <span>{{ o.answer }}</span><span>{{ o.total }} ({{ pct(o.total, r.total_answers) }}%)</span>
                  </div>
                  <div class="appui-bar"><div class="appui-bar-fill" :style="{ width: pct(o.total, r.total_answers) + '%' }"></div></div>
                </div>
              </template>
              <template v-else-if="r.asking_type === 4">
                <div style="font-size:22px;font-weight:800;color:#556ee6">{{ avgOf(r.responses) }} <small style="font-size:12px;color:#adb5bd;font-weight:500">média</small></div>
              </template>
              <template v-else>
                <div style="display:flex;flex-wrap:wrap;gap:6px">
                  <span v-for="(resp, i) in (r.responses || []).slice(0, 40)" :key="i" class="appui-badge">{{ resp }}</span>
                  <span v-if="(r.responses || []).length > 40" style="font-size:11px;color:#adb5bd">+{{ r.responses.length - 40 }} respostas</span>
                </div>
              </template>
            </div>
          </div>
        </section>
      </template>
    </div>

    <b-modal v-model="showModal" :title="form.id ? 'Editar pergunta' : 'Nova pergunta'" size="lg" hide-footer>
      <form @submit.prevent="save()">
        <div class="row">
          <div class="form-group col-sm-8"><label>Pergunta *</label><input type="text" class="form-control" v-model="form.asking" required></div>
          <div class="form-group col-sm-4">
            <label>Obrigatoriedade *</label>
            <select class="custom-select" v-model.number="form.required_answer"><option :value="1">Exige resposta</option><option :value="0">Opcional</option></select>
          </div>
        </div>
        <div class="row">
          <div class="form-group col-sm-8"><label>Descrição extra</label><input type="text" class="form-control" v-model="form.description"></div>
          <div class="form-group col-sm-4">
            <label>Tipo *</label>
            <select class="custom-select" v-model.number="form.asking_type">
              <option :value="1">Escolha única</option><option :value="2">Múltipla escolha</option>
              <option :value="3">Texto livre</option><option :value="4">Nota (avaliação)</option>
            </select>
          </div>
        </div>

        <div v-if="form.asking_type === 1 || form.asking_type === 2">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <label class="mb-0">Respostas</label>
            <a href="#" style="font-size:12.5px" @click.prevent="form.answers.push({ id: null, answer: '' })">+ Adicionar</a>
          </div>
          <div v-for="(o, i) in form.answers" :key="i" class="d-flex align-items-center mb-2" style="gap:10px">
            <input type="text" class="form-control" v-model="o.answer" placeholder="Resposta" required>
            <button type="button" class="appui-icon-btn appui-icon-btn--danger" @click="removeAnswer(i)"><i class="mdi mdi-trash-can-outline"></i></button>
          </div>
        </div>

        <div v-if="form.asking_type === 4" class="row">
          <div class="form-group col-sm-6"><label>Descrição avaliação mínima *</label><input type="text" class="form-control" v-model="form.min_rate_description" placeholder="ex: Muito ruim" required></div>
          <div class="form-group col-sm-6"><label>Classificação mínima *</label><input type="number" class="form-control" v-model.number="form.rate_min" required></div>
          <div class="form-group col-sm-6"><label>Descrição avaliação máxima *</label><input type="text" class="form-control" v-model="form.max_rate_description" placeholder="ex: Excelente" required></div>
          <div class="form-group col-sm-6"><label>Classificação máxima *</label><input type="number" class="form-control" v-model.number="form.rate_max" required></div>
        </div>

        <div class="text-right" style="margin-top:8px"><button type="button" class="btn btn-light mr-2" @click="showModal = false">Cancelar</button><button type="submit" class="btn btn-primary" :disabled="saving">Salvar</button></div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import services from './services'
import './app-ui.css'

const empty = () => ({ id: null, asking: '', description: '', asking_type: 1, required_answer: 1, answers: [{ id: null, answer: '' }, { id: null, answer: '' }], rate_min: 1, rate_max: 5, min_rate_description: '', max_rate_description: '' })

export default {
  components: { draggable },
  name: 'app-survey',
  data () {
    return { activeTab: 'perguntas', questions: [], results: [], showModal: false, saving: false, form: empty() }
  },
  mounted () { this.load() },
  methods: {
    switchTab (tab) { this.activeTab = tab; if (tab === 'resultados') this.loadResults() },
    load () { services.listSurvey().then(r => { this.questions = r.data.data }).catch(() => errorMessage('Falha ao carregar.', 3000)) },
    loadResults () { services.surveyResults().then(r => { this.results = r.data.data }).catch(() => {}) },
    typeLabel (t) { return { 1: 'Escolha única', 2: 'Múltipla', 3: 'Texto', 4: 'Nota' }[t] || t },
    typeIcon (t) { return { 1: 'mdi mdi-radiobox-marked', 2: 'mdi mdi-checkbox-multiple-marked-outline', 3: 'mdi mdi-text', 4: 'mdi mdi-star-outline' }[t] || 'mdi mdi-help' },
    pct (n, total) { return total ? Math.round((n / total) * 100) : 0 },
    avgOf (responses) {
      const nums = (responses || []).map(Number).filter(n => !isNaN(n))
      if (!nums.length) return '—'
      return (nums.reduce((s, n) => s + n, 0) / nums.length).toFixed(1)
    },
    openCreate () { this.form = empty(); this.showModal = true },
    openEdit (q) {
      this.form = {
        ...empty(),
        ...q,
        answers: (q.answers && q.answers.length ? q.answers.map(a => ({ id: a.id, answer: a.answer })) : [{ id: null, answer: '' }])
      }
      this.showModal = true
    },
    async removeAnswer (i) {
      if (this.form.answers.length <= 1) { errorMessage('Mantenha pelo menos uma resposta.', 2500); return }
      const ans = this.form.answers[i]
      if (ans.id && this.form.id) {
        const ok = await this.$bvModal.msgBoxConfirm('Remover esta resposta? As respostas já dadas a ela são mantidas nos resultados.', { okTitle: 'Remover', cancelTitle: 'Cancelar', okVariant: 'danger' })
        if (!ok) return
        try { await services.deleteSurveyAnswer(this.form.id, ans.id) } catch (e) { errorMessage('Falha ao remover resposta.', 3000); return }
      }
      this.form.answers.splice(i, 1)
    },
    async save () {
      this.saving = true
      try {
        const payload = {
          asking: this.form.asking,
          description: this.form.description || null,
          asking_type: this.form.asking_type,
          required_answer: this.form.required_answer,
          rate_min: this.form.rate_min,
          rate_max: this.form.rate_max,
          min_rate_description: this.form.min_rate_description || null,
          max_rate_description: this.form.max_rate_description || null,
          answers: this.form.answers.filter(a => (a.answer || '').trim())
        }
        if (this.form.id) await services.updateSurveyQuestion(this.form.id, payload)
        else await services.createSurveyQuestion(payload)
        this.showModal = false
        successMessage('Pergunta salva.', 2000)
        this.load()
      } catch (e) { errorMessage('Falha ao salvar.', 3000) } finally { this.saving = false }
    },
    async toggle (q) { try { await services.surveyQuestionStatus(q.id); q.status = q.status ? 0 : 1 } catch (e) { errorMessage('Falha.', 3000) } },
    async remove (q) {
      const ok = await this.$bvModal.msgBoxConfirm('Remover esta pergunta?', { okTitle: 'Remover', cancelTitle: 'Cancelar', okVariant: 'danger' })
      if (!ok) return
      try { await services.deleteSurveyQuestion(q.id); this.load() } catch (e) { errorMessage('Falha ao remover.', 3000) }
    },
    async reorder () {
      try { await services.reorderSurvey(this.questions.map(it => ({ id: it.id }))); this.load() } catch (e) { errorMessage('Falha ao reordenar.', 3000); this.load() }
    },
    async exportResults () {
      const loader = this.$loading.show()
      try {
        const res = await services.exportSurvey()
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.download = 'pesquisa-satisfacao.xlsx'
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (e) { errorMessage('Falha ao exportar.', 3000) } finally { loader.hide() }
    }
  }
}
</script>
